import React, { useState } from 'react';
import { Mail, Phone, UserMinus, X, AlertCircle, MessageSquare } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { FeedbackForm } from '../../feedback/components/FeedbackForm';
import { useMeusColaboradores, useDevolverColaborador } from '../hooks';
import type { User } from '../../../shared/lib/api';
import { useAfastamentosDaEquipe } from '../../pessoas/hooks/afastamentos';
import { CRITICALITY_LABELS, LEAVE_STATUS_LABELS, criticalityTone, formatDate } from '../../pessoas/components/format';
import { useFeriasDaEquipe } from '../../pessoas/hooks/ferias';
import { Link } from 'react-router-dom';

const MeusColaboradoresPage: React.FC = () => {
  const { data: collaborators = [], isLoading, isError } = useMeusColaboradores();
  const devolverColaborador = useDevolverColaborador();
  // Afastamentos da equipe sem dado médico (US-GP-008, visão do gestor).
  const afastados = useAfastamentosDaEquipe();
  // Férias da equipe direta (US-GP-014, seção 4.9).
  const ferias = useFeriasDaEquipe();

  const [devolverTarget, setDevolverTarget] = useState<User | null>(null);
  const [feedbackTarget, setFeedbackTarget] = useState<User | null>(null);
  const [motivo, setMotivo] = useState('');
  const [motivoError, setMotivoError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleOpenDevolver = (user: User) => {
    setDevolverTarget(user);
    setMotivo('');
    setMotivoError('');
  };

  const handleDevolverConfirm = async () => {
    if (!devolverTarget) return;
    if (!motivo.trim()) {
      setMotivoError('Informe o motivo da devolução');
      return;
    }
    setIsSubmitting(true);
    try {
      await devolverColaborador.mutateAsync({ collaboratorId: devolverTarget.id, motivo });
      setDevolverTarget(null);
      setMotivo('');
      setSuccessMessage(`${devolverTarget.name} devolvido com sucesso.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setMotivoError(err?.response?.data?.message || err?.message || 'Erro ao devolver colaborador.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackSuccess = () => {
    setFeedbackTarget(null);
    setSuccessMessage('Feedback salvo com sucesso!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Meus Colaboradores</h1>

      {(afastados.data ?? []).length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h2 className="mb-2 text-sm font-semibold text-amber-900">Afastamentos da equipe</h2>
          <ul className="space-y-1 text-sm text-amber-900">
            {(afastados.data ?? []).map((a) => (
              <li key={a.id}>
                {a.personName} — {a.typeName}, {formatDate(a.startDate)} a {formatDate(a.endDate)} ({LEAVE_STATUS_LABELS[a.status]})
              </li>
            ))}
          </ul>
        </section>
      )}

      {(ferias.data ?? []).length > 0 && (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Férias da equipe</h2>
            <Link to="/pessoas/aprovacoes-ferias" className="text-sm text-blue-700 hover:underline">Aprovações de férias</Link>
          </div>
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr><th className="py-1">Colaborador</th><th className="py-1">Disponível</th><th className="py-1">Próximas / em férias</th><th className="py-1">Vencimento</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(ferias.data ?? []).map((f) => (
                <tr key={f.userId}>
                  <td className="py-1.5"><Link className="text-blue-700 hover:underline" to={`/meus-colaboradores/${f.userId}/ferias`}>{f.name}</Link></td>
                  <td className="py-1.5">{f.available} dias</td>
                  <td className="py-1.5">
                    {f.onVacation ? 'Em férias' : f.installments[0] ? `${formatDate(f.installments[0].startDate)} a ${formatDate(f.installments[0].endDate)}` : '—'}
                  </td>
                  <td className="py-1.5">
                    {f.nearestConcessionEnd ? formatDate(f.nearestConcessionEnd) : '—'}
                    {f.criticality && (
                      <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${criticalityTone(f.criticality)}`}>{CRITICALITY_LABELS[f.criticality]}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro ao carregar colaboradores.</div>
      ) : collaborators.length === 0 ? (
        <div className="p-6 bg-gray-50 rounded-xl border border-gray-200 text-center text-sm text-gray-500">
          Nenhum colaborador vinculado à sua gestão.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regime</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {collaborators.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-700">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="flex items-center text-sm text-gray-700">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {user.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.employmentType ? (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.employmentType === 'pj'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.employmentType.toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setFeedbackTarget(user)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Feedback
                      </button>
                      <button
                        onClick={() => handleOpenDevolver(user)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <UserMinus className="w-4 h-4" />
                        Devolver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de feedback */}
      {feedbackTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Novo Feedback</h2>
                <p className="text-sm text-gray-500 mt-0.5">{feedbackTarget.name}</p>
              </div>
              <button onClick={() => setFeedbackTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <FeedbackForm
                initialCollaboratorId={feedbackTarget.id}
                onSuccess={handleFeedbackSuccess}
                onCancel={() => setFeedbackTarget(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de devolução */}
      {devolverTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Devolver Colaborador</h2>
              <button onClick={() => setDevolverTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Informe o motivo da devolução de{' '}
                <span className="font-semibold text-gray-900">{devolverTarget.name}</span>.
              </p>

              <TextInput
                label="Motivo *"
                value={motivo}
                onChange={e => { setMotivo(e.target.value); setMotivoError(''); }}
                error={motivoError}
              />

              {motivoError && !motivo && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {motivoError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setDevolverTarget(null)}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDevolverConfirm}
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Devolvendo...' : 'Devolver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeusColaboradoresPage;
